import Quill from "quill";
import { useRef, useLayoutEffect, useEffect } from "react";
import "quill/dist/quill.bubble.css";
import "quill/dist/quill.snow.css";
import "quill-mention/autoregister";
import "quill-mention/dist/quill.mention.css";
import "./QuillMention.overrides.css";
import classNames from "classnames";

export type MentionCandidate = { id: string; value: string };

type QuillEditorProps = {
  readOnly?: boolean;
  defaultValue?: any;
  onTextChange?: (...args: any[]) => void;
  onSelectionChange?: (...args: any[]) => void;
  parentRef: React.MutableRefObject<undefined | Quill | null>;
  theme?: "snow" | "bubble";
  initReadOnly?: boolean;
  disabled?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  mentionCandidates?: MentionCandidate[];
  toolbar?: HTMLElement | string | false;
  placeholder?: string;
};

const QuillEditor = ({
  readOnly,
  defaultValue,
  onTextChange,
  parentRef,
  onSelectionChange,
  theme = "snow",
  initReadOnly,
  disabled,
  onBlur,
  onFocus,
  className,
  mentionCandidates,
  toolbar,
  placeholder,
}: QuillEditorProps) => {
  const containerRef = useRef(null);
  const defaultValueRef = useRef(defaultValue);
  const onTextChangeRef = useRef(onTextChange);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const onBlurRef = useRef(onBlur);
  const onFocusRef = useRef(onFocus);
  const mentionCandidatesRef = useRef(mentionCandidates);
  const toolbarRef = useRef(toolbar);
  const placeholderRef = useRef(placeholder);

  useLayoutEffect(() => {
    onTextChangeRef.current = onTextChange;
    onSelectionChangeRef.current = onSelectionChange;
    onBlurRef.current = onBlur;
    onFocusRef.current = onFocus;
    mentionCandidatesRef.current = mentionCandidates;
  });

  useEffect(() => {
    if (!readOnly && !disabled && !initReadOnly) {
      parentRef?.current?.enable();
    } else {
      parentRef?.current?.disable();
    }
  }, [parentRef, disabled, readOnly, initReadOnly]);

  useEffect(() => {
    const container = containerRef.current as HTMLElement | null;
    const editorContainer = container?.appendChild(
      container?.ownerDocument.createElement("div")
    );

    if (editorContainer) {
      const mentionModule = mentionCandidatesRef.current
        ? {
            mention: {
              mentionDenotationChars: ["@"],
              positioningStrategy: "fixed",
              source: (
                searchTerm: string,
                renderList: (
                  matches: MentionCandidate[],
                  searchTerm: string
                ) => void
              ) => {
                const candidates = mentionCandidatesRef.current || [];
                const matches = searchTerm
                  ? candidates.filter((candidate) =>
                      candidate.value
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    )
                  : candidates;
                renderList(matches, searchTerm);
              },
            },
          }
        : undefined;

      let toolbarModule;
      if (toolbarRef.current === false) {
        toolbarModule = { toolbar: false };
      }
      if (toolbarRef.current) {
        toolbarModule = { toolbar: { container: toolbarRef.current } };
      }

      const modules =
        mentionModule || toolbarModule
          ? { ...mentionModule, ...toolbarModule }
          : undefined;

      const quill = new Quill(editorContainer, {
        theme: theme,
        readOnly: initReadOnly,
        placeholder: placeholderRef.current,
        modules,
      });

      parentRef.current = quill;

      if (defaultValueRef.current) {
        quill.setContents(defaultValueRef.current);
      }

      quill.on(Quill.events.TEXT_CHANGE, (delta) => {
        onTextChangeRef.current?.(delta);
      });

      quill.on(Quill.events.SELECTION_CHANGE, (range, oldRange, source) => {
        onSelectionChangeRef.current?.(range, oldRange, source);

        // Detect blur event when range is null
        if (range === null) {
          onBlurRef.current?.();
        } else {
          onFocusRef.current?.(); // Detect focus event when range is not null
        }
      });
    }

    return () => {
      parentRef.current = null;
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [parentRef]);

  return <div ref={containerRef} className={classNames(className)} />;
};

export default QuillEditor;
